import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/21e92476ad71fc9ba2123db9df7d7eb8/3ffc4ab5e308061b8151d721b5bb0a39.png"
                alt="GPsolutions"
                className="h-16 md:h-24 w-auto"
              />
            </Link>

            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/about"
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'
                }`}
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/projects"
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'
                }`}
              >
                {t('nav.projects')}
              </Link>
              <Link
                to="/contact"
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isScrolled ? 'text-gray-900 hover:text-gray-600' : 'text-white hover:text-gray-200'
                }`}
              >
                {t('nav.contact')}
              </Link>

              {/* Language Switcher */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                    i18n.language === 'en'
                      ? isScrolled
                        ? 'text-gray-900'
                        : 'text-white'
                      : isScrolled
                      ? 'text-gray-400 hover:text-gray-600'
                      : 'text-white/60 hover:text-white/80'
                  }`}
                >
                  EN
                </button>
                <span className={isScrolled ? 'text-gray-400' : 'text-white/60'}>|</span>
                <button
                  onClick={() => changeLanguage('hr')}
                  className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap ${
                    i18n.language === 'hr'
                      ? isScrolled
                        ? 'text-gray-900'
                        : 'text-white'
                      : isScrolled
                      ? 'text-gray-400 hover:text-gray-600'
                      : 'text-white/60 hover:text-white/80'
                  }`}
                >
                  HR
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}
              aria-label="Toggle menu"
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="fixed top-[104px] left-0 right-0 md:hidden bg-white shadow-lg z-40 max-h-[calc(100vh-104px)] overflow-y-auto">
          <div className="px-8 py-6 space-y-4">
            <Link
              to="/"
              className="block py-3 text-gray-900 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/about"
              className="block py-3 text-gray-900 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/projects"
              className="block py-3 text-gray-900 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.projects')}
            </Link>
            <Link
              to="/contact"
              className="block py-3 text-gray-900 hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.contact')}
            </Link>
            
            <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  changeLanguage('en');
                  setIsMobileMenuOpen(false);
                }}
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded ${
                  i18n.language === 'en' ? 'bg-gray-900 text-white' : 'hover:text-gray-600'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => {
                  changeLanguage('hr');
                  setIsMobileMenuOpen(false);
                }}
                className={`text-sm font-light tracking-wide transition-colors whitespace-nowrap cursor-pointer px-3 py-2 rounded ${
                  i18n.language === 'hr' ? 'bg-gray-900 text-white' : 'hover:text-gray-600'
                }`}
              >
                HR
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
